import React, { useState } from 'react';
import './Styles.css';
import { formatMessage } from '../../../../../Localization/formatMessage';
import Messages from '../../../../../Localization/Messages';
import DraggableModal from '../../../../../Elements/DraggableModal';
import ErrorMessage from '../../../../../Elements/ErrorMessage';
import CheckBox from '../../../../../Elements/ids/InputElements/CheckBox';
import {
  areBrokerFunctionsAvailable,
  canAgentNameBeSet,
  canCommissionEnabledBeSet,
  canEmployerBeSet,
} from '../../../Functions';
import { BusinessArea } from '../../../../../Enums/BusinessArea';
import { useLegalUnitName } from '../../../../../Helpers/CustomHooks';
import { useGlobalContext } from '../../../../../context/GlobalContext';
import { ServiceClient } from '../../../../../Services/ServiceClient';
import { createServerErrorMessage } from '../../../../../Helpers/FormInputHelper';
import { teamPageReducerTypes } from '../../../Reducer';
import { useTeamPageContext } from '../../../TeamPage';
import HistoricalAgentInfoButtons from './Buttons';

const HistoricalAgentInfo = ({ closeView, agent, team }) => {
  const { businessArea } = useGlobalContext();
  const { teamPageDispatch } = useTeamPageContext();
  const legalUnitName = useLegalUnitName(team);
  const [error, setError] = useState();

  return (
    <DraggableModal
      titleText={formatMessage(Messages.historicalAgents_modalTitle, { agentCode: agent.code })}
      closeView={closeView}
      buttons={
        <HistoricalAgentInfoButtons
          error={error}
          closeView={closeView}
          onClickEvent={() => {
            ServiceClient.reactivateAgent(
              agent.id,
              reactivatedAgent => {
                const newAgent = {
                  ...reactivatedAgent,
                  address: agent?.address,
                  addressIsInherited: agent?.addressIsInherited,
                };
                const agents = team.agents.filter(x => x.id !== reactivatedAgent.id);
                const newTeam = { ...team, agents: [...agents, newAgent] };
                teamPageDispatch({
                  type: teamPageReducerTypes.updateTeamInfo,
                  payload: newTeam,
                });
                closeView();
              },
              error => {
                setError(createServerErrorMessage(error).message);
              }
            );
          }}
        />
      }>
      <div className="teamPage-historicalAgentInfo-container">
        <div className="teamPage-historicalAgentInfo-item">
          <div className="teamPage-historicalAgentInfo-itemTitle">{formatMessage(Messages.agentInfo_channel)}</div>
          <div>{agent.channel.name}</div>
        </div>
        <div className="teamPage-historicalAgentInfo-item">
          <div className="teamPage-historicalAgentInfo-itemTitle">{formatMessage(Messages.agentInfo_region)}</div>
          <div>{agent.region.name}</div>
        </div>
        <div className="teamPage-historicalAgentInfo-item">
          <div className="teamPage-historicalAgentInfo-itemTitle">{formatMessage(Messages.agentInfo_costCenter)}</div>
          <div>{agent.costCenter.name}</div>
        </div>

        {canAgentNameBeSet(legalUnitName) && (
          <div className="teamPage-historicalAgentInfo-item">
            <div className="teamPage-historicalAgentInfo-itemTitle">{formatMessage(Messages.common_name)}</div>
            <div>{agent.name}</div>
          </div>
        )}

        <div className="teamPage-historicalAgentInfo-item">
          <div className="teamPage-historicalAgentInfo-itemTitle">{formatMessage(Messages.common_email)}</div>
          <div>{agent.email}</div>
        </div>

        <div className="teamPage-historicalAgentInfo-item">
          <div className="teamPage-historicalAgentInfo-itemTitle">{formatMessage(Messages.common_phone)}</div>
          <div>{agent.phone}</div>
        </div>

        <div className="teamPage-historicalAgentInfo-item">
          <div className="teamPage-historicalAgentInfo-itemTitle">{formatMessage(Messages.common_mobile)}</div>
          <div>{agent.mobile}</div>
        </div>

        <div className="teamPage-historicalAgentInfo-item">
          <div className="teamPage-historicalAgentInfo-itemTitle">
            {formatMessage(Messages.common_externalSalesCode)}
          </div>
          <div>{agent.externalSalesCode}</div>
        </div>

        {(businessArea ?? businessArea) === BusinessArea.Commercial && (
          <div className="teamPage-historicalAgentInfo-item">
            <div className="teamPage-historicalAgentInfo-itemTitle">
              {formatMessage(Messages.common_additionalName)}
            </div>
            <div>{agent.additionalName}</div>
          </div>
        )}

        {canEmployerBeSet(team.employers) && (
          <div className="teamPage-historicalAgentInfo-item">
            <div className="teamPage-historicalAgentInfo-itemTitle">{formatMessage(Messages.agentInfo_employer)}</div>
            <div>{agent.employer?.name}</div>
          </div>
        )}

        {canCommissionEnabledBeSet(legalUnitName) && (
          <>
            <CheckBox
              disabled={true}
              cssClasses="teamPage-historicalAgentInfo-checkBoxLabel"
              label={formatMessage(Messages.agentInfo_salesCommission)}
              checked={agent.commissionEnabled}
              checkBoxId={'commissionEnabled'}
            />

            <div className="teamPage-historicalAgentInfo-item">
              <div className="teamPage-historicalAgentInfo-itemTitle">
                {formatMessage(Messages.agentInfo_employeeNumber)}
              </div>
              <div>{agent.employeeNumber}</div>
            </div>
          </>
        )}

        {areBrokerFunctionsAvailable(team) && (
          <>
            <div className="teamPage-historicalAgentInfo-item">
              <div className="teamPage-historicalAgentInfo-itemTitle">
                {formatMessage(Messages.agentInfo_groupEmail)}
              </div>
              <div>{agent.groupEmail}</div>
            </div>

            <CheckBox
              disabled={true}
              cssClasses="teamPage-historicalAgentInfo-checkBoxLabel"
              label={formatMessage(Messages.agentInfo_digital)}
              checked={agent.extension?.isDigitalCustomer}
              checkBoxId={'isDigitalCustomer'}
            />

            <CheckBox
              disabled={true}
              cssClasses="teamPage-historicalAgentInfo-checkBoxLabel"
              label={formatMessage(Messages.agentInfo_xmlExport)}
              checked={agent.extension?.availableForXmlExport}
              checkBoxId={'availableForXmlExport'}
            />
          </>
        )}

        <CheckBox
          disabled={true}
          cssClasses="teamPage-historicalAgentInfo-checkBoxLabel"
          label={formatMessage(Messages.agentInfo_canBePortfolioResponsible)}
          checked={agent.canBePortfolioResponsible}
          checkBoxId={'canBePortfolioResponsible'}
        />

        <ErrorMessage message={error} />
      </div>
    </DraggableModal>
  );
};

export default HistoricalAgentInfo;
